// Shared Gemini client for the multi-pane Career CLI.
//
// One singleton GoogleGenAI instance is lazily constructed from
// VITE_GEMINI_API_KEY the first time `streamChat` is called. Every pane
// (careerCoach, techInspector) shares this single client and
// streams its own conversation through the same model.
//
// Model: `gemini-2.5-flash` (matches existing geminiService.ts).
//
// Error handling philosophy:
//   - Never throw from `streamChat`. Surface failures by yielding a single
//     human-readable string so the pane keeps rendering.
//   - Missing API key is the most common path in local/preview builds; we
//     handle it explicitly with an explanatory message.

import { GoogleGenAI } from '@google/genai';
import type { ChatMessage } from './AgentConfig';

const MODEL_NAME = 'gemini-2.5-flash';

// Singleton — lazily constructed on first call so a missing key doesn't
// trip a module-level error during import.
let clientPromise: { client: GoogleGenAI | null; reason?: string } | null = null;

function getClient(): { client: GoogleGenAI | null; reason?: string } {
  if (clientPromise !== null) return clientPromise;

  const apiKey =
    (import.meta as unknown as { env?: Record<string, string | undefined> }).env
      ?.VITE_GEMINI_API_KEY;

  if (!apiKey) {
    clientPromise = {
      client: null,
      reason:
        'Gemini API key is not configured. Set VITE_GEMINI_API_KEY in your env to enable chat.',
    };
    return clientPromise;
  }

  try {
    const client = new GoogleGenAI({ apiKey });
    clientPromise = { client };
  } catch (err) {
    clientPromise = {
      client: null,
      reason: `Failed to initialize Gemini client: ${
        err instanceof Error ? err.message : String(err)
      }`,
    };
  }
  return clientPromise;
}

/**
 * Convert our ChatMessage[] history into the SDK's `Content[]` shape.
 *
 * Role mapping:
 *   - 'user'   -> 'user'
 *   - 'agent'  -> 'model'
 *   - 'system' -> dropped (already embedded into systemInstruction)
 *   - 'tool'   -> dropped (slash-command output, not part of the LLM context)
 */
function toContents(history: ChatMessage[]) {
  const out: { role: 'user' | 'model'; parts: { text: string }[] }[] = [];
  for (const m of history) {
    if (m.role === 'user') {
      out.push({ role: 'user', parts: [{ text: m.text }] });
    } else if (m.role === 'agent') {
      out.push({ role: 'model', parts: [{ text: m.text }] });
    }
    // system / tool messages are local-only.
  }
  return out;
}

/**
 * Stream a Gemini response token-by-token.
 *
 * Yields plain strings (the incremental text deltas). Consumers append each
 * yielded chunk to the in-progress agent message.
 *
 * If the client is unavailable, yields a single explanatory line and exits.
 */
export async function* streamChat(
  systemPrompt: string,
  history: ChatMessage[],
  userText: string,
): AsyncIterable<string> {
  const { client, reason } = getClient();
  if (!client) {
    yield reason ?? 'Chat is unavailable right now.';
    return;
  }

  // Build the full contents list: prior history + this turn's user message.
  const contents = [
    ...toContents(history),
    { role: 'user' as const, parts: [{ text: userText }] },
  ];

  try {
    const stream = await client.models.generateContentStream({
      model: MODEL_NAME,
      contents,
      config: {
        systemInstruction: systemPrompt,
        temperature: 0.7,
        topK: 40,
        topP: 0.95,
        maxOutputTokens: 2048,
      },
    });

    let sawText = false;
    for await (const chunk of stream) {
      const text = chunk.text;
      if (text) {
        sawText = true;
        yield text;
      }
    }

    if (!sawText) {
      yield '(no response — try rephrasing your question)';
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    yield `\n[error] Gemini request failed: ${msg}`;
  }
}

export const GEMINI_MODEL_NAME = MODEL_NAME;
